import { formatCents } from '@auctisync/shared';

/**
 * Module #23 chunk 5 — the derived "fun facts" shown on a lot.
 *
 * WHY THIS IS SHARED RATHER THAN A COMPONENT ON EACH SURFACE. The website had
 * this as `LotFunFacts.tsx`, deriving the facts inline in its own JSX. Building
 * the native equivalent by re-reading that JSX and retyping the conditions is
 * exactly how the website and the app drift: the thresholds ("10 views", "above
 * starting bid") and the wording are product decisions, and a product decision
 * implemented twice is a product decision that will eventually disagree with
 * itself. Rules live once — see the no-mechanical-divergence rule in CLAUDE.md.
 *
 * So this module owns WHICH facts apply and WHAT they say. Each surface owns
 * only how they look.
 *
 * Scope is the lot's own engagement and price fields. Cross-tenant sales-history
 * aggregates ("12 similar sold in 30d") remain a follow-up — they need a
 * SECURITY DEFINER RPC over intelligence.platform_aggregates with a k>=5
 * anonymisation floor, which is a privacy decision, not a display one.
 */
export interface LotFactInput {
  bidCount: number | null | undefined;
  currentBidCents: number | null | undefined;
  startingBidCents: number | null | undefined;
  watchCount?: number | null;
  viewCount?: number | null;
  quantity?: number | null;
}

/** Minimum views before the count is worth showing — below this it reads as dead. */
const VIEW_FLOOR = 10;

/**
 * Facts for a lot, in display order, most concrete first. Empty when nothing
 * applies — a brand-new lot with no bids and no watches should show nothing
 * rather than a row of zeroes, which would make it look less appealing than
 * saying nothing at all.
 */
export function lotFunFacts(input: LotFactInput): string[] {
  const facts: string[] = [];

  if ((input.quantity ?? 0) > 1) {
    facts.push(`Multi-unit: ${input.quantity} units available`);
  }

  // Only meaningful once someone has actually bid — otherwise "0 above
  // starting bid" is noise on every unbid lot in the sale.
  if (
    (input.bidCount ?? 0) > 0 &&
    input.currentBidCents != null &&
    input.startingBidCents != null &&
    input.currentBidCents > input.startingBidCents
  ) {
    facts.push(`${formatCents(input.currentBidCents - input.startingBidCents)} above starting bid`);
  }

  // Watch counters are populated by triggers that have not shipped yet, so this
  // is 0 across every lot today. Degrading to "absent" rather than "0 watching"
  // is what lets it ship before the counters do.
  if ((input.watchCount ?? 0) > 0) {
    facts.push(`${input.watchCount} watching this lot`);
  }

  if ((input.viewCount ?? 0) >= VIEW_FLOOR) {
    facts.push(`${input.viewCount} views`);
  }

  return facts;
}

/** What the bidder-facing MSRP line is called, on every surface. */
export const MSRP_LABEL = 'MSRP';

/**
 * The manufacturer's suggested retail price, formatted for a bidder — or null
 * when there is nothing honest to show.
 *
 * Jim, 2026-09-23: "MSRP should be visible to the bidder." It is the anchor a
 * bidder prices against, and the cataloguing AI now researches it for every lot.
 *
 * Null for a missing, zero or negative value: "$0.00 MSRP" on a lot nobody
 * researched would read as a claim that the item is worth nothing.
 */
export function lotMsrp(msrpCents: number | null | undefined): string | null {
  if (msrpCents == null || !Number.isFinite(msrpCents) || msrpCents <= 0) return null;
  return formatCents(msrpCents);
}
